// src/pages/Onboarding/slides/RedDisplaySlide.js

import React, { useState, useRef, useCallback, useEffect } from 'react';
import SlideShell from '../SlideShell';
import Popup from '../../../components/Popup/Popup';

// Import the SVG images
import brainImage from '../../../images/brain.svg';
import digImage from '../../../images/dig.svg';
import warningLabelImage from '../../../images/warning_label.svg';

// Red theme background color (matching the design)
const RED_BG_COLOR = '#D8042A';

/**
 * RED DISPLAY SLIDE - TRIPLE SUB-SLIDE COMPONENT
 * 
 * A red-themed display slide with three horizontal sub-slides:
 * 1. "Hair loss causes extreme stress" (brain icon)
 * 2. "Hair loss destroys confidence" (dig icon)
 * 3. "Hair loss gets worse over time" (warning icon)
 * 
 * Features:
 * - Manual swipe/scroll between sub-slides
 * - Dot indicator at bottom of content area
 * - Continue button advances sub-slide until last, then goes to next step
 * 
 * PROPS:
 * - currentStep/totalSteps: For progress bar
 * - onNext: Callback to advance to next onboarding step
 * - onBack: Back button callback
 * - animationDirection/isTransitioning: For slide animations
 */

export default function RedDisplaySlide({
  currentStep,
  totalSteps,
  onNext,
  onBack,
  animationDirection = 'forward',
  isTransitioning = false,
}) {
  // Track which sub-slide is active (0, 1, or 2)
  const [activeSubSlide, setActiveSubSlide] = useState(0);
  const [isSubSlideTransitioning, setIsSubSlideTransitioning] = useState(false);
  const containerRef = useRef(null);
  
  // Sources popup state
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);
  const openSources = (e) => { e?.stopPropagation?.(); setIsSourcesOpen(true); };
  const closeSources = () => setIsSourcesOpen(false);
  
  // Touch/swipe tracking
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isDragging = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);

  // Sub-slide data
  const subSlides = [
    {
      title: 'Hair loss causes extreme stress',
      description: '85% of men experience hair loss and resulting stress before the age of 50.',
      imageSrc: brainImage,
      imageAlt: 'Brain with lightning bolt illustration',
    },
    {
      title: 'Hair loss destroys confidence',
      description: 'Balding hurts your confidence, affecting romantic relationships, social interactions and self respect.',
      imageSrc: digImage,
      imageAlt: 'Confidence illustration',
    },
    {
      title: 'Hair loss gets worse over time',
      description: "Majority of men don't know what to do about their hair loss, leading to inaction that causes even further hair loss.",
      imageSrc: warningLabelImage,
      imageAlt: 'Warning label illustration',
    },
  ];

  // Navigate to a specific sub-slide
  const goToSubSlide = useCallback((index) => {
    if (index === activeSubSlide || isSubSlideTransitioning) return;
    if (index < 0 || index >= subSlides.length) return;
    
    setIsSubSlideTransitioning(true);
    setActiveSubSlide(index);
    setTimeout(() => {
      setIsSubSlideTransitioning(false);
    }, 300);
  }, [activeSubSlide, isSubSlideTransitioning, subSlides.length]);

  // Handle touch start
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
    isDragging.current = true;
  }, []);

  // Handle touch move
  const handleTouchMove = useCallback((e) => {
    if (!isDragging.current) return;
    touchEndX.current = e.touches[0].clientX;
    const diff = touchEndX.current - touchStartX.current;
    // Limit drag offset to prevent over-scrolling
    const containerWidth = containerRef.current?.offsetWidth || 300;
    const maxDrag = containerWidth * 0.5;
    const clampedDiff = Math.max(-maxDrag, Math.min(maxDrag, diff));
    setDragOffset(clampedDiff);
  }, []);

  // Handle touch end
  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    
    const diff = touchEndX.current - touchStartX.current;
    const threshold = 50; // Minimum swipe distance
    
    if (diff > threshold && activeSubSlide > 0) {
      // Swipe right - go to previous sub-slide
      goToSubSlide(activeSubSlide - 1);
    } else if (diff < -threshold && activeSubSlide < subSlides.length - 1) {
      // Swipe left - go to next sub-slide
      goToSubSlide(activeSubSlide + 1);
    }
    
    setDragOffset(0);
  }, [activeSubSlide, goToSubSlide, subSlides.length]);

  // Handle mouse events for desktop swipe
  const handleMouseDown = useCallback((e) => {
    touchStartX.current = e.clientX;
    touchEndX.current = e.clientX;
    isDragging.current = true;
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    touchEndX.current = e.clientX;
    const diff = touchEndX.current - touchStartX.current;
    const containerWidth = containerRef.current?.offsetWidth || 300;
    const maxDrag = containerWidth * 0.5;
    const clampedDiff = Math.max(-maxDrag, Math.min(maxDrag, diff));
    setDragOffset(clampedDiff);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    
    const diff = touchEndX.current - touchStartX.current;
    const threshold = 50;
    
    if (diff > threshold && activeSubSlide > 0) {
      goToSubSlide(activeSubSlide - 1);
    } else if (diff < -threshold && activeSubSlide < subSlides.length - 1) {
      goToSubSlide(activeSubSlide + 1);
    }
    
    setDragOffset(0);
  }, [activeSubSlide, goToSubSlide, subSlides.length]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      setDragOffset(0);
    }
  }, []);

  // Handle Continue button click - advances sub-slide until last, then goes to next step
  const handleContinue = () => {
    if (activeSubSlide < subSlides.length - 1) {
      // Not on last sub-slide: advance to next sub-slide
      goToSubSlide(activeSubSlide + 1);
    } else {
      // Last sub-slide: advance to next onboarding step
      onNext();
    }
  };

  // Handle back button
  const handleBack = () => {
    if (activeSubSlide > 0) {
      // On any sub-slide after first: go back to previous sub-slide
      goToSubSlide(activeSubSlide - 1);
    } else {
      // On first sub-slide: go to previous onboarding step
      onBack();
    }
  };

  // Calculate transform including drag offset
  const getTransform = () => {
    const baseOffset = activeSubSlide * 100;
    const containerWidth = containerRef.current?.offsetWidth || 300;
    const dragPercent = (dragOffset / containerWidth) * 100;
    return `translateX(calc(-${baseOffset}% + ${dragPercent}%))`;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: RED_BG_COLOR }}>
      <div style={{ backgroundColor: RED_BG_COLOR }}>
        <SlideShell
          title={null}
          subtitle={null}
          currentStep={currentStep}
          totalSteps={totalSteps}
          onNext={handleContinue}
          onBack={handleBack}
          nextLabel="Continue"
          disabled={isSubSlideTransitioning}
          theme="dark"
          animationDirection={animationDirection}
          isTransitioning={isTransitioning}
          primaryNote={
            <span>
              Based on research by{' '}
              <button
                type="button"
                className="underline underline-offset-2 transition-colors text-white/80 hover:text-white"
                onClick={openSources}
              >
                Harvard
              </button>
              {', '}
              <button
                type="button"
                className="underline underline-offset-2 transition-colors text-white/80 hover:text-white"
                onClick={openSources}
              >
                PubMed
              </button>
              {' and '}
              <button
                type="button"
                className="underline underline-offset-2 transition-colors text-white/80 hover:text-white"
                onClick={openSources}
              >
                American Hair Loss Association
              </button>
              .
            </span>
          }
          primaryNoteClassName="text-white/80"
          backgroundStyle={{ backgroundColor: RED_BG_COLOR }}
          abovePrimaryContent={
            <div className="flex items-center justify-center gap-0.5 pb-2">
              {subSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSubSlide(index)}
                  className="relative p-1"
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full transition-all duration-300 ease-out ${
                      index === activeSubSlide
                        ? 'bg-white scale-125'
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                  />
                </button>
              ))}
            </div>
          }
        >
          {/* Swipeable container */}
          <div 
            ref={containerRef}
            className="relative overflow-hidden w-full flex flex-col"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: 'grab', userSelect: 'none' }}
          >
            {/* Sliding content */}
            <div
              className={`flex ${isDragging.current ? '' : 'transition-transform duration-300 ease-out'}`}
              style={{
                transform: getTransform(),
              }}
            >
              {subSlides.map((slide, index) => (
                <div
                  key={index}
                  className="w-full flex-shrink-0 flex flex-col items-center text-center space-y-4 py-8 px-4"
                >
                  {/* Central Image */}
                  <div className="w-32 h-32 lg:w-48 lg:h-48 flex items-center justify-center">
                    <img
                      src={slide.imageSrc}
                      alt={slide.imageAlt}
                      className="w-full h-full object-contain pointer-events-none"
                      draggable={false}
                    />
                  </div>

                  {/* Title */}
                  <h1 className="text-xl lg:text-2xl font-bold leading-tight px-2 text-white">
                    {slide.title}
                  </h1>

                  {/* Description */}
                  <p className="text-sm lg:text-base leading-relaxed px-6 max-w-md text-white/80">
                    {slide.description}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </SlideShell>

        {/* Unified Sources Popup — all entries follow the Harvard format */}
        <Popup isOpen={isSourcesOpen} onClose={closeSources} className="text-gray-900">
          <div className="p-6 space-y-6">
            <h3 className="text-xl font-semibold">Sources & citations</h3>

            {/* Harvard */}
            <section>
              <h4 className="text-base font-semibold mb-2 underline">Harvard Stem Cell Institute</h4>
              <p className="text-sm font-bold">
                How chronic stress leads to hair loss.
              </p>
              <p className="text-xs mt-1 text-gray-600">
                Choi, S. et&nbsp;al. (2021). Corticosterone inhibits GAS6 to govern hair follicle stem cell quiescence. <i>Nature</i>.
                DOI: 10.1038/s41586-021-03417-2
              </p>
              <p className="text-xs mt-1 text-gray-600">
                URL: https://www.hsci.harvard.edu/news/how-chronic-stress-leads-to-hair-loss
              </p>
            </section>

            {/* PubMed */}
            <section>
              <h4 className="text-base font-semibold mb-2 underline">PubMed</h4>

              <article className="mb-4">
                <p className="text-sm font-bold">
                  Association between alopecia areata, anxiety, and depression: A systematic review and meta-analysis.
                </p>
                <p className="text-xs mt-1 text-gray-600">
                  Okhovat JP, Marks DH, Manatis-Lornell A, Hagigeorges D, Locascio JJ, Senna MM. <i>J Am Acad Dermatol.</i>
                  2023 May;88(5):1040–1050. DOI: 10.1016/j.jaad.2019.05.086. PMID: 31163237.
                </p>
                <p className="text-xs mt-1 text-gray-600">
                  URL: https://pubmed.ncbi.nlm.nih.gov/31163237/
                </p>
              </article>

              <article className="mb-4">
                <p className="text-sm font-bold">
                  The tendency towards the development of psychosexual disorders in androgenetic alopecia according to the different
                  stages of hair loss: a cross-sectional study.
                </p>
                <p className="text-xs mt-1 text-gray-600">
                  Tas B, Kulacaoglu F, Belli H, Altuntas M. <i>An Bras Dermatol.</i> 2018 Mar;93(2):185–190.
                  DOI: 10.1590/abd1806-4841.20185658. PMID: 29723381; PMCID: PMC5916388.
                </p>
                <p className="text-xs mt-1 text-gray-600">
                  URL: https://pubmed.ncbi.nlm.nih.gov/29723381/
                </p>
              </article>

              <article className="mb-0">
                <p className="text-sm font-bold">
                  The impact, prevalence, and association of different forms of hair loss among individuals with anxiety disorder:
                  Systematic review and meta-analysis.
                </p>
                <p className="text-xs mt-1 text-gray-600">
                  Almudimeegh A, Alajlan AH, Alrasheed AI, Alrasheed MI, Alqahtani AK, Idris RB, Alomar MA, Alobaid SA, Alotaibi NA.
                  <i>Medicine (Baltimore).</i> 2025 Feb 7;104(6):e41457. DOI: 10.1097/MD.0000000000041457. PMID: 39928820; PMCID: PMC11813004.
                </p>
                <p className="text-xs mt-1 text-gray-600">
                  URL: https://pubmed.ncbi.nlm.nih.gov/39928820/
                </p>
              </article>
            </section>

            {/* AHLA */}
            <section>
              <h4 className="text-base font-semibold mb-2 underline">American Hair Loss Association</h4>
              <p className="text-sm font-bold">
                Men's Hair Loss — American Hair Loss Association.
              </p>
              <p className="text-xs mt-1 text-gray-600">
                American Hair Loss Association. Accessed 2025.
              </p>
              <p className="text-xs mt-1 text-gray-600">
                URL: https://www.americanhairloss.org/mens-hair-loss/
              </p>
            </section>
          </div>
        </Popup>
      </div>
    </div>
  );
}
